import React, { useState, useEffect } from 'react';
import { getDataBS, deleteDataBS } from '../../utils/awsService';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import { TestTube2 } from 'lucide-react';

const PaymentPanel = () => {
  const [PaymentTests, setPaymentTests] = useState([]);
  const [filteredPaymentTests, setFilteredPaymentTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPaymentTests = async () => {
      try {
        setLoading(true);
        setError(null);

        const responseData = await getDataBS('/payment');

        // Extract Payment test data
        let PaymentData = responseData.data['data'];

        // Transform data to extract specific fields
        const processedPaymentTests = PaymentData.map(test => ({
          id: test.id || null,
          fullName: test.name|| 'Anonymous',
          email: test.email || '',
          submissionDate: test.submitted_date || new Date().toISOString(),
          ContactNo: test.number,
          amount: test.amount
        }));

        // Sort based on submission date
        const sortedTests = processedPaymentTests.sort((a, b) => {
          const dateA = new Date(a.submissionDate);
          const dateB = new Date(b.submissionDate);
          return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        setPaymentTests(sortedTests);
        setFilteredPaymentTests(sortedTests);
      } catch (error) {
        setError(error.message || 'Failed to fetch Payment tests');
        setPaymentTests([]);
        setFilteredPaymentTests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentTests();
    const intervalId = setInterval(fetchPaymentTests, 30000);
    
    return () => clearInterval(intervalId);
  }, [sortOrder]);

  // Update filtered results when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPaymentTests(PaymentTests);
    } else {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      const filtered = PaymentTests.filter(
        test => 
          (test.fullName && test.fullName.toLowerCase().includes(lowercaseSearchTerm)) ||
          (test.ContactNo && test.ContactNo.includes(searchTerm))
      );
      setFilteredPaymentTests(filtered);
    }
  }, [searchTerm, PaymentTests]);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'newest' ? 'oldest' : 'newest');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    // Remove leading 0 if present
    if (number.startsWith('0')) {
      number = number.slice(1);
    }
  
    // Format as XXXX-XXX-XXX
    return number.replace(/(\d{4})(\d{3})(\d{3})/, '$1-$2-$3');
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return <div className="loading-container">Loading Payment submissions...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const handleView = (id) => {
    navigate(`/admin/responses/Payment/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this Payment test submission?')) {
      try {
        await deleteDataBS('/payment', id);
        
        setPaymentTests(prevTests => 
          prevTests.filter(test => test.id !== id)
        );
      } catch (error) {
        console.error('Error deleting Payment test:', error);
        alert('Failed to delete Payment test.');
      }
    }
  };

  const handleBack = () => {
    navigate('/admin/admin-dashboard');
  };

  return (
    <div className="admin-container">
      <button onClick={handleBack} className="back-btn">← Back to Dashboard</button>

      <h1 className="admin-title">Payment Submission Admin Panel</h1>
      
      <div className="controls-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name or contact number"
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>
      
      {filteredPaymentTests.length === 0 ? (
        <div className="no-data-container">
          {searchTerm.trim() !== '' ? (
            <p className="no-data">No results found for "{searchTerm}".</p>
          ) : (
            <p className="no-data">No Payment test submissions found.</p>
          )}
          <button onClick={() => window.location.reload()} className="retry-btn">
            Reload Data
          </button>
        </div>
      ) : (
        <div className="consultations-grid">
          {filteredPaymentTests.map((test) => (
            <div key={test.id} className="consultation-card">
              <div className="card-header">
                <h3 className="client-name">{test.fullName}</h3>
              </div>
              <div className="card-content">
                <p>Email: {test.email}</p>
                <p>Amount: {test.amount}</p>
                <p>ContactNo: {formatPhoneNumber(test.ContactNo)}</p>
                <p>Submission Date: {formatDate(test.submissionDate)}</p>
              </div>
              <div className="card-actions">
                <button 
                  onClick={() => handleView(test.id)}
                  className="view-btn"
                >
                  View Details
                </button>
                <button 
                  onClick={() => handleDelete(test.id)}
                  className="delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentPanel;